import type React from "react";
import {
	Button,
	CheckBox,
	Input,
	InputCode,
	PhoneInput,
	Radio,
} from "@haidilao-private/mobile-design";
import { LOGIN_EMAIL, LOGIN_PHONE } from "../util";
import "./LoginCouponModal.scss";

export interface AreaCodeOption {
	key: string;
	label: string;
	// PhoneInput 展示用，格式为“国家名称（区号）”。
	value: string;
	// 业务请求只需要纯区号，避免把展示文案传回父组件。
	code?: string;
	description?: string;
	disabled?: boolean;
}

interface LoginCouponModalProps {
	visible: boolean;
	translate: any;
	loginType: string;
	isEmailLogin: boolean;
	phone: string;
	email: string;
	verifyCode: string;
	phoneAreaCode: string;
	areaCodeOptions: AreaCodeOption[];
	isAgree: boolean;
	isAgreePush: boolean;
	isGetVerCode: boolean;
	verCodeText: React.ReactNode;
	canGetVerifyCode: boolean;
	canReceive: boolean;
	languageCode: string;
	onClose: () => void;
	onLoginTypeChange: (value: string) => void;
	onPhoneChange: (value: string) => void;
	onEmailChange: (value: string) => void;
	onVerifyCodeChange: (value: string) => void;
	onAreaCodeChange: (option: AreaCodeOption) => void;
	onGetVerifyCode: () => void;
	onAgreeChange: (checked: boolean) => void;
	onAgreePushChange: (checked: boolean) => void;
	onPrivacyClick: () => void;
	onMemberNoticeClick: () => void;
	onReceive: () => void;
}

const LoginCouponModal: React.FC<LoginCouponModalProps> = ({
	visible,
	translate,
	loginType,
	isEmailLogin,
	phone,
	email,
	verifyCode,
	phoneAreaCode,
	areaCodeOptions,
	isAgree,
	isAgreePush,
	isGetVerCode,
	verCodeText,
	canGetVerifyCode,
	canReceive,
	languageCode,
	onClose,
	onLoginTypeChange,
	onPhoneChange,
	onEmailChange,
	onVerifyCodeChange,
	onAreaCodeChange,
	onGetVerifyCode,
	onAgreeChange,
	onAgreePushChange,
	onPrivacyClick,
	onMemberNoticeClick,
	onReceive,
}) => {
	if (!visible) return null;

	// PhoneInput 通过 key 控制选中项，父组件仍用纯区号保存业务状态。
	const selectedAreaCodeKey =
		areaCodeOptions.find((option) => option.code === phoneAreaCode)?.key ||
		phoneAreaCode;
	// 倒计时中禁用；未输入账号时只禁用按钮，不提前触发格式校验提示。
	const isVerifyCodeButtonDisabled = isGetVerCode || !canGetVerifyCode;

	return (
		<div className="loginCouponModal" onClick={onClose}>
			<div
				className="loginCouponModalSheet"
				onClick={(event) => {
					event.stopPropagation();
					const target = event.target as HTMLElement;
					// 组件库仅原生 input 可聚焦；让手机号边框内的留白也响应点击。
					// 保留输入框、清空按钮和区号按钮的原生交互。
					if (target.closest("input, button")) return;
					const field = target.closest(
						".loginCouponModalPhoneInput .components_src_components_input_index__field",
					);
					field?.querySelector<HTMLInputElement>("input:not(:disabled)")?.focus();
				}}
			>
				<div className="loginCouponModalHeader">
					<div className="loginCouponModalTitle">
						{translate && translate["领取优惠券"]}
					</div>
					<button
						type="button"
						className="loginCouponModalClose"
						onClick={onClose}
					>
						×
					</button>
				</div>

				<div className="loginCouponModalContent">
					<div className="loginCouponModalTip">
						{translate && translate["未注册的用户将直接创建账户"]}
					</div>

					<div className="loginCouponModalAccountType">
						<div className="loginCouponModalFieldLabel">
							{translate && translate["账号类型​"]}
						</div>
						<div className="loginCouponModalRadioGroup">
							<Radio
								name="loginType"
								value={LOGIN_PHONE}
								label={translate && translate[LOGIN_PHONE]}
								checked={loginType === LOGIN_PHONE}
								onChange={(checked) => {
									if (checked) onLoginTypeChange(LOGIN_PHONE);
								}}
							/>
							<Radio
								name="loginType"
								value={LOGIN_EMAIL}
								label={translate && translate[LOGIN_EMAIL]}
								checked={loginType === LOGIN_EMAIL}
								onChange={(checked) => {
									if (checked) onLoginTypeChange(LOGIN_EMAIL);
								}}
							/>
						</div>
					</div>

					{isEmailLogin ? (
						<Input
							className="loginCouponModalInput"
							label={translate && translate["邮箱"]}
							placeholder={translate && translate["邮箱"]}
							value={email}
							clearable
							onChange={onEmailChange}
						/>
					) : (
						<PhoneInput
							className="loginCouponModalPhoneInput"
							areaCodeLabel=""
							areaCodeDialogTitle={translate && translate["手机号"]}
							areaCodeValue={phoneAreaCode}
							areaCodeOptions={areaCodeOptions}
							areaCodeKey={selectedAreaCodeKey}
							selectionDialogClassName="loginCouponModalAreaCodeDialog"
							phoneLabel={translate && translate["手机号"]}
							placeholder={translate && translate["手机号"]}
							value={phone}
							maxLength={11}
							clearable
							onCountryChange={onAreaCodeChange}
							onChange={onPhoneChange}
						/>
					)}

					<div className="loginCouponModalCodeSection">
						{isGetVerCode || verifyCode ? (
							<InputCode
								className="loginCouponModalInputCode"
								value={verifyCode.split("").slice(0, 6)}
								length={6}
								// InputCode 使用数组值，父组件业务状态仍保持字符串，避免扩大现有校验和接口影响范围。
								onChangeCode={(code) =>
									onVerifyCodeChange(code.join("").slice(0, 6))
								}
								onComplete={(code) => onVerifyCodeChange(code.slice(0, 6))}
							/>
						) : null}
						<Button
							className={[
								"loginCouponModalCodeButton",
								!isVerifyCodeButtonDisabled
									? "loginCouponModalCodeButtonActive"
									: "",
							].join(" ")}
							type="filled"
							size="md"
							text={verCodeText}
							disabled={isVerifyCodeButtonDisabled}
							onClick={onGetVerifyCode}
						/>
					</div>

					<div className="loginCouponModalAgreement">
						<CheckBox
							checked={isAgree}
							onChange={onAgreeChange}
							label={
								<span>
									<span>
										{translate &&
											translate["我已成年并具备完全民事行为能力，同意"]}
									</span>
									<button
										type="button"
										className="loginCouponModalLink"
										onClick={(event) => {
											event.stopPropagation();
											onPrivacyClick();
										}}
									>
										{translate && translate["隐私政策"]}
									</button>
									<span>{translate && translate["和"]}</span>
									<button
										type="button"
										className="loginCouponModalLink"
										onClick={(event) => {
											event.stopPropagation();
											onMemberNoticeClick();
										}}
									>
										{translate && translate["会员须知"]}
									</button>
									{languageCode === "KR" || languageCode === "JP" ? (
										<span>{translate && translate["age_tip_1"]}</span>
									) : null}
								</span>
							}
						/>
						<CheckBox
							checked={isAgreePush}
							onChange={onAgreePushChange}
							label={
								translate &&
								translate["本人同意以注册手机号或邮箱接收海底捞推送信息"]
							}
						/>
					</div>
				</div>
				<div className="loginCouponModalFooter">
					<Button
						className="loginCouponModalReceiveButton"
						type="solid"
						size="lg"
						block
						text={translate && translate["立即领取"]}
						disabled={!canReceive}
						onClick={onReceive}
					/>
				</div>
			</div>
		</div>
	);
};

export default LoginCouponModal;
